import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import Button from "@mui/material/Button";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";
import CustomTextField from "../../../UI/CustomTextField";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { supabase } from "../../../../supabase";
import { useMainContext } from "../../../../context/useMainContext";
import { getTableForColumn } from "../../hooks/useTableColumnsConfig";

export default function TextFieldEdit() {
  const {
    setRows,
    isGuest,
    textFieldEditModalOpen,
    setTextFieldEditModalOpen,
    currentTextFieldData,
    setCurrentTextFieldData,
    saveUndoData,
    setLoadingData,
    loadingData,
  } = useMainContext();

  const [localValue, setLocalValue] = useState("");

  // Update local value when modal opens
  useEffect(() => {
    if (currentTextFieldData) {
      setLocalValue(currentTextFieldData.value || "");
    }
  }, [currentTextFieldData]);

  const handleSave = async () => {
    if (!currentTextFieldData) return;
    const { row, col } = currentTextFieldData;
    const previousValue = row[col] || "";
    const tableName = getTableForColumn({ columnKey: col });

    const saveData = async (value) => {
      try {
        setLoadingData(true);
        let error;
        if (tableName === "influencers") {
          ({ error } = await supabase
            .from("influencers")
            .update({
              [col]: value,
            })
            .eq("id", row.id));
        } else {
          const { data: influencer, error: DbError } = await supabase
            .from("influencers")
            .select("id")
            .eq("id", row.id)
            .single();
          if (DbError) {
            toast.error("Failed to find influencer");
            return DbError;
          }
          ({ error } = await supabase.from(tableName).upsert(
            {
              influencer_id: influencer.id,
              [col]: value,
            },
            { onConflict: "influencer_id" }
          ));
        }
        if (!error) {
          setRows((prevRows) =>
            prevRows.map((r) =>
              r.id === row.id
                ? {
                    ...r,
                    [col]: value,
                  }
                : r
            )
          );
        }
        return error;
      } finally {
        setLoadingData(false);
      }
    };

    const error = await saveData(localValue);
    if (error) {
      toast.error(`Failed to update ${col}`);
    } else {
      toast.success(`Updated ${col}`);
      if (col === "firstname") {
        saveUndoData({
          action: "First name updated",
          undoFunction: () => saveData(previousValue),
          redoFunction: () => saveData(localValue),
        });
      }
      handleClose();
    }
  };

  const handleClose = () => {
    setTextFieldEditModalOpen(false);
    setCurrentTextFieldData(null);
    setLocalValue("");
  };

  const handleCancel = () => {
    handleClose();
  };

  return (
    <Dialog open={textFieldEditModalOpen} onClose={handleCancel} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Edit {currentTextFieldData?.col || "Field"}
        <IconButton onClick={handleCancel} size="small" aria-label="Close dialog">
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <CustomTextField
          fullWidth
          multiline
          rows={3}
          placeholder={`Enter ${currentTextFieldData?.col || "value"}...`}
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          disabled={loadingData}
          autoFocus
        />
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 2 }}>
        <Button variant="outlined" onClick={handleCancel} disabled={loadingData} fullWidth>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={loadingData || isGuest} fullWidth disableElevation>
          {loadingData ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
